import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  X, 
  Loader2,
  Building2,
  User,
  Calendar,
  MapPin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CreateContractModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    numero_contrato: '',
    contractor_id: '',
    inspector_ids: [],
    fecha_inicio: '',
    fecha_fin: '',
    ubicacion: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const contractors = users.filter(u => u.role === 'contractor');
  const inspectors = users.filter(u => u.role === 'inspector');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre del contrato es requerido');
      return;
    }
    
    // Contratista y residentes son opcionales: se pueden asignar después desde USUARIOS

    setLoading(true);
    try {
      const response = await axios.post(`${API}/projects`, formData);
      toast.success(`Contrato "${formData.name}" creado exitosamente`);
      onSuccess?.(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error(error.response?.data?.detail || 'Error al crear contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleInspectorChange = (inspectorId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        inspector_ids: [...prev.inspector_ids, inspectorId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        inspector_ids: prev.inspector_ids.filter(id => id !== inspectorId)
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-[#0066FF]" />
            <h2 className="text-xl font-bold text-[#E2EAF4]">Nuevo Contrato</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-[#E2EAF4] hover:bg-[#1E3550]"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Nombre del contrato */}
          <div>
            <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
              Nombre del Contrato *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: Edificio Torre Norte"
              className="w-full bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
            />
          </div>

          {/* Número de contrato */}
          <div>
            <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
              Número de Contrato Oficial
            </label>
            <Input
              value={formData.numero_contrato}
              onChange={(e) => setFormData(prev => ({ ...prev, numero_contrato: e.target.value }))}
              placeholder="Ej: CONT-2024-001"
              className="w-full bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
            />
          </div>

          {/* Contratista */}
          <div>
            <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Contratista Asignado
            </label>
            <select
              value={formData.contractor_id}
              onChange={(e) => setFormData(prev => ({ ...prev, contractor_id: e.target.value }))}
              className="w-full h-10 px-3 rounded-md border border-[#1E3550] bg-[#0A1422] text-[#E2EAF4] text-sm"
              data-testid="select-contractor"
            >
              <option value="">Sin asignar (asignar después)</option>
              {contractors.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
              ))}
            </select>
            {contractors.length === 0 && (
              <p className="text-xs text-[#5A7A9A] mt-1" data-testid="no-contractors-hint">
                Aún no hay contratistas. Crea el contrato ahora y asigna usuarios después desde Admin → USUARIOS.
              </p>
            )}
          </div>

          {/* Residentes */}
          <div>
            <label className="block text-sm font-medium text-[#E2EAF4] mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Residentes Asignados
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto border border-[#1E3550] rounded-md p-2 bg-[#0A1422]">
              {inspectors.length === 0 ? (
                <p className="text-sm text-[#5A7A9A]">No hay residentes disponibles</p>
              ) : (
                inspectors.map(inspector => (
                  <label key={inspector.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.inspector_ids.includes(inspector.id)}
                      onChange={(e) => handleInspectorChange(inspector.id, e.target.checked)}
                      className="rounded border-[#1E3550] bg-[#0A1422]"
                    />
                    <span className="text-sm text-[#E2EAF4]">{inspector.name}</span>
                    <span className="text-xs text-[#5A7A9A]">({inspector.email})</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha Inicio
              </label>
              <Input
                type="date"
                value={formData.fecha_inicio}
                onChange={(e) => setFormData(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                className="w-full bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha Fin Estimada
              </label>
              <Input
                type="date"
                value={formData.fecha_fin}
                onChange={(e) => setFormData(prev => ({ ...prev, fecha_fin: e.target.value }))}
                className="w-full bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
              />
            </div>
          </div>

          {/* Ubicación */}
          <div>
            <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Ubicación / Descripción de Obra
            </label>
            <textarea
              value={formData.ubicacion}
              onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
              placeholder="Ej: Av. Insurgentes Sur 1234, Col. Del Valle, CDMX"
              className="w-full h-20 px-3 py-2 rounded-md border border-[#1E3550] bg-[#0A1422] text-[#E2EAF4] text-sm resize-none placeholder:text-[#5A7A9A]"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 flex justify-end gap-3 bg-[#0A1422]">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="border-[#1E3550] text-[#E2EAF4] hover:bg-[#1E3550]"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="create-contract-submit"
            className="bg-[#0066FF] hover:bg-[#0066FF]/80 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              'Crear Contrato'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateContractModal;
